import { createTheme } from '@mui/material/styles';
import { colors } from './colors';

// @ts-ignore
import OpenSansBoldWoff from './fonts/OpenSans-Bold.woff';
// @ts-ignore
import OpenSansBoldItalicWoff from './fonts/OpenSans-BoldItalic.woff';
// @ts-ignore
import OpenSansExtraBoldWoff from './fonts/OpenSans-ExtraBold.woff';
// @ts-ignore
import OpenSansExtraBoldItalicWoff from './fonts/OpenSans-ExtraBoldItalic.woff';
// @ts-ignore
import OpenSansItalicWoff from './fonts/OpenSans-Italic.woff';
// @ts-ignore
import OpenSansLightWoff from './fonts/OpenSans-Light.woff';
// @ts-ignore
import OpenSansLightItalicWoff from './fonts/OpenSans-LightItalic.woff';
// @ts-ignore
import OpenSansRegularWoff from './fonts/OpenSans-Regular.woff';
// @ts-ignore
import OpenSansSemiBoldWoff from './fonts/OpenSans-Semibold.woff';
// @ts-ignore
import OpenSansSemiBoldItalicWoff from './fonts/OpenSans-SemiboldItalic.woff';

const OpenSansBold = {
  fontFamily: 'OpenSansBold',
  src: `
    url(${OpenSansBoldWoff}) format('woff');
  `,
};

const OpenSansBoldItalic = {
  fontFamily: 'OpenSansBoldItalic',
  src: `
    url(${OpenSansBoldItalicWoff}) format('woff');
  `,
};

const OpenSansExtraBold = {
  fontFamily: 'OpenSansExtraBold',
  src: `
    url(${OpenSansExtraBoldWoff}) format('woff');
  `,
};

const OpenSansExtraBoldItalic = {
  fontFamily: 'OpenSansExtraBoldItalic',
  src: `
    url(${OpenSansExtraBoldItalicWoff}) format('woff');
  `,
};

const OpenSansItalic = {
  fontFamily: 'OpenSansItalic',
  src: `
    url(${OpenSansItalicWoff}) format('woff');
  `,
};

const OpenSansLight = {
  fontFamily: 'OpenSansLight',
  src: `
    url(${OpenSansLightWoff}) format('woff');
  `,
};

const OpenSansLightItalic = {
  fontFamily: 'OpenSansLightItalic',
  src: `
    url(${OpenSansLightItalicWoff}) format('woff');
  `,
};

const OpenSansRegular = {
  fontFamily: 'OpenSansRegular',
  src: `
    url(${OpenSansRegularWoff}) format('woff');
  `,
};

const OpenSansSemiBold = {
  fontFamily: 'OpenSansSemiBold',
  src: `
    url(${OpenSansSemiBoldWoff}) format('woff');
  `,
};

const OpenSansSemiBoldItalic = {
  fontFamily: 'OpenSansSemiBoldItalic',
  src: `
    url(${OpenSansSemiBoldItalicWoff}) format('woff');
  `,
};

const baseFontSize = 14; // .875rem

/** @see https://mui.com/customization/breakpoints/ */
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true; // retains the `xs` breakpoint
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
  }
  // Add Frankie colors to MUI theme
  // https://mui.com/customization/theming/#custom-variables
  interface Theme {
    frankie: {
      primary: string;
      quinary: string;
      senary: string;
      quaternary: string;
      secondary: string;
      neonYellow: string;
      searchBlue: string;
    };
  }
  interface ThemeOptions {
    frankie?: {
      primary?: string;
      quinary?: string;
      senary?: string;
      quaternary?: string;
      secondary?: string;
      neonYellow?: string;
      searchBlue?: string;
    };
  }
}

export const theme = createTheme({
  spacing: 16, // 1rem
  palette: {
    common: {
      black: '#000000',
      white: '#fff',
    },
    primary: {
      main: colors.green,
      contrastText: '#fff',
    },
    secondary: {
      main: colors.lightBlue,
      contrastText: '#fff',
    },
    error: {
      main: '#A94442',
    },
    warning: {
      main: '#8A6D3B',
    },
    info: {
      main: '#31708F',
    },
    success: {
      main: '#3C763D',
    },
    background: {
      default: '#ffffff',
    },
  },
  // NOTE: New colors here must be added above in interface Theme{,Options}
  frankie: {
    primary: colors.green,
    quinary: colors.darkOrange,
    senary: colors.mistyGreen,
    quaternary: colors.orangeBrown,
    secondary: colors.dullBlue,
    searchBlue: colors.searchBlue,
    neonYellow: '#CCDB2ADE',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 767,
      tablet: 1500,
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        body: {
          borderBottom: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        // Frankie AdminApp main buttons are color="secondary" + variant="contained"
        // Search "<Button" to see where in the AdminApp directory
        containedSecondary: {
          backgroundColor: colors.searchBlue,
          '&:hover': {
            backgroundColor: '#68A2B8',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '13px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
        shrink: {
          marginTop: '2px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '@font-face': [
            OpenSansBold,
            OpenSansBoldItalic,
            OpenSansExtraBold,
            OpenSansExtraBoldItalic,
            OpenSansItalic,
            OpenSansLight,
            OpenSansLightItalic,
            OpenSansRegular,
            OpenSansSemiBold,
            OpenSansSemiBoldItalic,
          ],
        },
      },
    },
  },
  typography: {
    fontFamily: 'OpenSansRegular, sans-serif',
    fontSize: baseFontSize,
    h1: {
      fontSize: baseFontSize * 2.25,
      fontFamily: 'OpenSansSemiBold, sans-serif',
    },
    h2: {
      fontSize: baseFontSize * 2,
      fontFamily: 'OpenSansSemiBold, sans-serif',
    },
    h3: {
      fontSize: baseFontSize * 1.75,
      fontFamily: 'OpenSansRegular, sans-serif',
    },
    h4: {
      fontSize: baseFontSize * 1.5,
      fontFamily: 'OpenSansRegular, sans-serif',
    },
    h5: {
      fontSize: baseFontSize * 1.25,
      fontFamily: 'OpenSansRegular, sans-serif',
    },
  },
});
